/**
 * State Serializer
 *
 * Hybrid state serialization strategy:
 * - Small states (<1.5KB): Direct URL encoding (shareable across browsers/devices)
 * - Large states (>=1.5KB): localStorage with short ID (shareable across tabs, 24h TTL)
 * - Fallback: Always supports direct URL encoding for backward compatibility
 *
 * Features:
 * - Base64 URL-safe encoding for direct mode
 * - localStorage with 8-char unique IDs for large states
 * - 24-hour TTL with automatic cleanup
 * - Automatic validation with Zod
 * - Graceful degradation if storage unavailable
 *
 * URL Length Translation (Base64 encoding, ~133% of JSON size):
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * JSON Size    →  Base64      →  URL Length  →  Storage Method
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 100 bytes        ~133 chars     ~233 chars     Direct URL ✅
 * 500 bytes        ~667 chars     ~767 chars     Direct URL ✅
 * 1KB              ~1.3K chars    ~1.4K chars    Direct URL ✅
 * 1.5KB (thresh)   ~2K chars      ~2.1K chars    localStorage 💾
 * 2KB+             N/A            ~20 chars      localStorage 💾
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * Use cases:
 * - Deep linking to any step with pre-filled data
 * - State persistence across page refreshes and tabs
 * - Share-able links for testing/debugging (works across tabs)
 * - Jump functionality in DevTool (any size)
 * - Resume flows exactly where you left off (24h window)
 */

import { z } from "zod";
import {
  generateStateId,
  retrieveState,
  storeState,
  updateState,
} from "../lib/state-storage";

/**
 * Serialized state schema
 * Only includes what's needed to resume the flow
 */
const SerializedStateSchema = z.object({
  // Current state name (string for simple states, object for nested compound states)
  value: z.union([z.string(), z.record(z.unknown())]),

  // Machine context (all accumulated data)
  context: z.record(z.unknown()),

  // Optional: arc-internal state for DevTool jump-to-arc-state
  // Present only when copying from inside a child arc actor.
  arc: z
    .object({
      arcId: z.string(),
      stateName: z.union([z.string(), z.record(z.unknown())]),
      context: z.record(z.unknown()),
    })
    .optional(),

  // Optional: Schema version for migration
  _v: z.literal(1).default(1),
});

export type SerializedState = z.infer<typeof SerializedStateSchema>;

/**
 * Threshold for switching from direct URL to sessionStorage
 * States larger than this will use sessionStorage + short ID
 */
export const URL_DIRECT_THRESHOLD = 1500; // bytes (~1.5KB JSON)

/**
 * Serialize machine state to URL-safe string
 *
 * Strategy:
 * 1. Extract minimal state (value + context)
 * 2. JSON stringify
 * 3. Base64 encode and make URL-safe
 *
 * @param state - Current XState machine state
 * @returns URL-safe Base64 encoded string
 */
export function serializeState(state: {
  value: string | object;
  context: Record<string, unknown>;
}): string {
  const serialized: SerializedState = {
    value: state.value as string | Record<string, unknown>,
    context: state.context,
    _v: 1,
  };

  const json = JSON.stringify(serialized);

  // Base64 encode (UTF-8 safe) and make URL-safe
  // Convert to UTF-8 bytes first to handle Unicode characters
  const utf8Bytes = new TextEncoder().encode(json);
  const binaryString = Array.from(utf8Bytes, (byte) =>
    String.fromCharCode(byte),
  ).join("");
  const base64 = btoa(binaryString);
  const urlSafe = base64
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "~");

  return urlSafe;
}

/**
 * Deserialize URL-safe string to machine state
 *
 * @param encoded - URL-safe Base64 encoded string
 * @returns Deserialized state or null if invalid
 */
export function deserializeState(encoded: string): SerializedState | null {
  try {
    // Decode URL-safe chars back to base64
    const base64 = encoded
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .replace(/~/g, "=");

    // Decode base64 to binary string, then convert to UTF-8
    const binaryString = atob(base64);
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);

    const parsed = JSON.parse(json);

    // Validate against schema
    const result = SerializedStateSchema.safeParse(parsed);

    if (!result.success) {
      console.error("[StateSerializer] Invalid state format:", result.error);
      return null;
    }

    return result.data;
  } catch (error) {
    console.error("[StateSerializer] Failed to deserialize state:", {
      error,
      encoded: encoded.substring(0, 100),
    });
    return null;
  }
}

/**
 * Check if encoded state is valid
 */
export function isValidEncodedState(encoded: string | null): boolean {
  if (!encoded) return false;
  return deserializeState(encoded) !== null;
}

/**
 * Generate shareable URL with current state
 *
 * @param state - Current machine state
 * @param baseUrl - Base URL (defaults to current origin + pathname)
 * @returns Full URL with ?state=xxx parameter
 */
export function generateStateUrl(
  state: { value: string | object; context: Record<string, unknown> },
  baseUrl?: string,
): string {
  const encoded = serializeState(state);
  const base =
    baseUrl ?? `${window.location.origin}${window.location.pathname}`;
  return `${base}?state=${encoded}`;
}

/**
 * Serialize state with hybrid strategy (localStorage or direct URL)
 *
 * Strategy:
 * - Always use localStorage for clean URLs (keeps URLs short and aesthetic)
 * - Store state in localStorage, return short 8-char ID
 * - Fallback to direct base64 encoding only if localStorage fails
 *
 * @param state - Current machine state
 * @param existingStateId - Optional existing state ID to reuse (for updates during same session)
 * @returns Object with encoded string and storage method used
 */
export function serializeStateHybrid(
  state: {
    value: string | object;
    context: Record<string, unknown>;
    arc?: {
      arcId: string;
      stateName: string | Record<string, unknown>;
      context: Record<string, unknown>;
    };
  },
  existingStateId?: string,
): {
  encoded: string;
  method: "direct" | "storage";
  size: number;
} {
  const serialized: SerializedState = {
    value: state.value as string | Record<string, unknown>,
    context: state.context,
    ...(state.arc && { arc: state.arc }),
    _v: 1,
  };

  const json = JSON.stringify(serialized);
  const size = new Blob([json]).size;

  // If we have an existing state ID, update it instead of creating new one
  if (existingStateId) {
    const updated = updateState(existingStateId, serialized);
    if (updated) {
      return {
        encoded: existingStateId,
        method: "storage",
        size,
      };
    }
    // If update failed, fall through to create new state
  }

  // Always use localStorage for clean URLs (no size threshold check)
  const stateId = generateStateId();
  const stored = storeState(stateId, serialized);

  return {
    encoded: stateId,
    method: "storage",
    size,
  };
}

/**
 * Deserialize state with hybrid strategy (tries both methods)
 *
 * Strategy:
 * 1. If exactly 8 chars: Must be a state ID → sessionStorage only
 * 2. If longer: Must be base64 encoded state → direct decode only
 * 3. This avoids trying to base64 decode state IDs
 *
 * @param encoded - Either a stateId (8 chars) or base64 encoded state (longer)
 * @returns Deserialized state or null
 */
export function deserializeStateHybrid(
  encoded: string,
): SerializedState | null {
  if (!encoded) return null;

  // Check if it's exactly 8 chars = state ID for sessionStorage
  const isStateId = /^[a-zA-Z0-9]{8}$/.test(encoded);

  if (isStateId) {
    const fromStorage = retrieveState(encoded);
    if (fromStorage) {
      return fromStorage;
    }
    console.warn(
      "[StateSerializer] ❌ State ID not found in sessionStorage (expired or different session):",
      encoded,
    );
    return null; // Don't try base64 decode on state IDs
  }

  const fromDirect = deserializeState(encoded);
  if (fromDirect) {
    return fromDirect;
  }

  console.warn(
    "[StateSerializer] ❌ Failed to decode base64 state:",
    encoded.substring(0, 50),
  );
  return null;
}

/**
 * Copy state URL to clipboard using hybrid strategy
 *
 * @param state - Current machine state
 * @param existingStateId - Optional existing state ID to reuse
 * @returns Promise that resolves with storage method used
 */
export async function copyStateToClipboard(
  state: {
    value: string | object;
    context: Record<string, unknown>;
  },
  existingStateId?: string,
): Promise<{ method: "direct" | "storage"; size: number }> {
  const { encoded, method, size } = serializeStateHybrid(
    state,
    existingStateId,
  );
  const base = `${window.location.origin}${window.location.pathname}`;

  // Use 'state' param for direct encoding, 'stateId' for storage
  const paramName = method === "direct" ? "state" : "stateId";
  const url = `${base}?${paramName}=${encoded}`;

  await navigator.clipboard.writeText(url);

  return { method, size };
}

/**
 * Get size information about serialized state
 *
 * @param state - Current machine state
 * @returns Size information including URL length
 */
export function getSerializedStateSize(state: {
  value: string | object;
  context: Record<string, unknown>;
}): {
  encoded: number;
  uncompressed: number;
  urlLength: number;
  encodingRatio: number;
  isWithinRecommendedLimit: boolean;
  isWithinServerLimit: boolean;
  estimatedFields: number;
} {
  const serialized: SerializedState = {
    value: state.value as string | Record<string, unknown>,
    context: state.context,
    _v: 1,
  };

  const json = JSON.stringify(serialized);
  const encoded = serializeState(state);

  const uncompressed = new Blob([json]).size;
  const encodedSize = encoded.length; // Character count for URL

  // Estimate full URL length (base URL + query params + state)
  // Typical: https://site.com/kiosk?practiceId=123&appointmentId=456&state=XXXXX
  const baseUrlEstimate = 100; // ~100 chars for domain + path + other params
  const fullUrlLength = baseUrlEstimate + encodedSize;

  const RECOMMENDED_LIMIT = 2048; // 2KB (~2048 chars)
  const SERVER_LIMIT = 8192; // 8KB (~8192 chars)

  // Estimate number of form fields based on context size
  const contextKeys = Object.keys(state.context).length;

  return {
    encoded: encodedSize,
    uncompressed: uncompressed,
    urlLength: fullUrlLength,
    encodingRatio: Math.round((encodedSize / uncompressed) * 100),
    isWithinRecommendedLimit: fullUrlLength <= RECOMMENDED_LIMIT,
    isWithinServerLimit: fullUrlLength <= SERVER_LIMIT,
    estimatedFields: contextKeys,
  };
}
